import React, { Component, Fragment, useState, useEffect } from 'react';
import { Link, PageContent, Toolbar, Sheet } from 'framework7-react';
import CanvasDraw from 'react-canvas-draw';
import PropType from 'prop-types';

import { I18n } from 'Locales';
import { Button } from 'Components';
import { mockFunction } from 'Helpers';
import { ColumnView } from 'Containers';

import style from './style';

class SignatureCanvas extends Component {
  render = () => {
    const { width } = this.props;

    return (
      <PageContent>
        <CanvasDraw
          backgroundColor="#282626"
          brushColor="#FFF"
          ref={canvasDraw => (this.saveableCanvas = canvasDraw)}
          brushRadius={2}
          lazyRadius={0}
          canvasWidth={width}
          canvasHeight={width / 2}
          hideGrid={true}
        />
      </PageContent>
    );
  };
}

SignatureCanvas.propTypes = {
  width: PropType.number
};

const Signature = props => {
  const { currentPageIndex, parentRef, setSheetVisible, sheetVisible, shouldShowError, updateSignature, value } = props;

  const [canvasRef, attachRef] = useState({});
  const [width, setWidth] = useState(400);

  useEffect(() => {
    setWidth(parentRef.current.offsetWidth ? parentRef.current.offsetWidth : 400);
  }, [currentPageIndex]);

  const signatureSrc =
    value ||
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAIAAAABCAIAAAB7QOjd' +
      'AAAAD0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

  return (
    <div className="h-full w-full">
      <ColumnView flex={1} alignSelf={'flex-start'} onClick={setSheetVisible.bind(null, true)}>
        <div
          slot="media"
          style={{ ...style.signatureBox, ...(shouldShowError && style.redBorder) }}
          className="flex w-full">
          <img className="flex w-full" src={signatureSrc} style={style.signatureImg} alt="Signature" />
          {!value && <div style={style.signaturePlaceholder}>{I18n.t('general:tapToSign')}</div>}
        </div>
      </ColumnView>

      <Sheet opened={sheetVisible} onSheetClosed={setSheetVisible.bind(null, false)} style={style.signatureSheetStyle}>
        {sheetVisible && (
          <Fragment>
            <Toolbar>
              <div className="left">
                <Button.Transparent onClick={canvasRef?.saveableCanvas?.clear || mockFunction}>
                  {I18n.t('general:clear')}
                </Button.Transparent>
              </div>
              <div className="right ">
                <Link
                  textColor="white"
                  sheetClose
                  onClick={doneEvent.bind(null, canvasRef?.saveableCanvas, updateSignature)}>
                  {I18n.t('general:done')}
                </Link>
              </div>
            </Toolbar>

            <SignatureCanvas width={width} ref={attachRef} />
          </Fragment>
        )}
      </Sheet>
    </div>
  );
};

const doneEvent = async (saveableCanvas, updateSignature) => {
  if (!saveableCanvas) return;

  const data = saveableCanvas.getSaveData();
  if (JSON.parse(data).lines.length === 0) return;

  const canvases = saveableCanvas.canvasContainer?.querySelectorAll?.('canvas');
  const drawingCanvas = canvases?.[1] || saveableCanvas.canvasContainer?.children?.[1] || canvases?.[0];

  if (!drawingCanvas?.toDataURL) return;

  const rawPng = drawingCanvas.toDataURL('image/png');

  const exportPng = await toBlackInkTransparentBackground(rawPng, '#282626');

  updateSignature(exportPng);
};

const toBlackInkTransparentBackground = (dataUrl, bgHex = '#282626') =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('No 2D context'));

      ctx.drawImage(img, 0, 0);

      const { r: br, g: bg, b: bb } = hexToRgb(bgHex);
      const bgLum = luminance(br, bg, bb);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = imageData.data;

      for (let i = 0; i < d.length; i += 4) {
        const r = d[i];
        const g = d[i + 1];
        const b = d[i + 2];
        const a = d[i + 3];

        if (a === 0) continue;

        const lum = luminance(r, g, b);
        const inferredCoverage = clamp01((lum - bgLum) / (255 - bgLum)); // 0..1
        const aNorm = a / 255;

        d[i] = 0;
        d[i + 1] = 0;
        d[i + 2] = 0;
        d[i + 3] = Math.round(inferredCoverage * aNorm * 255);
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = reject;
    img.src = dataUrl;
  });

const hexToRgb = hex => {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map(c => c + c)
          .join('')
      : clean;
  const num = parseInt(full, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255
  };
};

const luminance = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;
const clamp01 = x => Math.max(0, Math.min(1, x));

Signature.propTypes = {
  currentPageIndex: PropType.number,
  mode: PropType.string,
  parentRef: PropType.any,
  setSheetVisible: PropType.func,
  sheetVisible: PropType.bool,
  shouldShowError: PropType.bool,
  updateAgreement: PropType.func,
  updateSignature: PropType.func,
  userFullName: PropType.string,
  value: PropType.oneOfType([PropType.string, PropType.object])
};

export default Signature;
